import * as React from 'react';
import { InfoCircleIcon } from '../icons/Info';
import { cn } from '../../utils/cn';
import { Tooltip, TooltipProps } from './Tooltip';
import { TOOLTIP_ARROW, type TooltipArrow } from './styles';

export interface InfoTooltipProps extends Omit<TooltipProps, 'children'> {
  /**
   * Additional class to apply to the icon
   */
  iconClassName?: string;
  
  /**
   * Test ID for the icon
   */
  iconDataTestId?: string;
}

/**
 * InfoTooltip Component
 * 
 * A component that combines an information icon with a tooltip.
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  iconClassName,
  iconDataTestId,
  dataTestId,
  ...tooltipProps
}) => {
  // Keep arrow direction but adjust tooltip position to align arrow with icon
  const normalizeArrow = (arrow?: TooltipArrow): TooltipArrow => {
    if (!arrow) return TOOLTIP_ARROW.TOP_CENTER;
    return arrow;
  };

  // Calculate horizontal offset to align arrow with icon center
  const getAlignOffset = (arrow?: TooltipArrow): number => {
    const normalized = normalizeArrow(arrow);
    
    // For left/right positioned arrows, shift tooltip so arrow points at icon
    if (normalized === TOOLTIP_ARROW.TOP_LEFT || normalized === TOOLTIP_ARROW.BOTTOM_LEFT) {
      return -16; // Shift left by 16px (visually aligned)
    }
    if (normalized === TOOLTIP_ARROW.TOP_RIGHT || normalized === TOOLTIP_ARROW.BOTTOM_RIGHT) {
      return -16; // Shift left by 16px (visually aligned) - right arrows need negative offset
    }
    
    return 0; // No offset for center/left/right arrows
  };

  return (
    <Tooltip
      delayDuration={0}
      {...tooltipProps}
      arrow={normalizeArrow(tooltipProps.arrow)}
      alignOffset={getAlignOffset(tooltipProps.arrow)}
    >
      <span 
        className={cn(
          'inline-flex items-center justify-center cursor-pointer',
          'text-primary',
          iconClassName
        )} 
        data-testid={iconDataTestId || dataTestId}
      >
        <InfoCircleIcon size="xs" />
      </span>
    </Tooltip>
  );
};

InfoTooltip.displayName = 'InfoTooltip';
